/**
 * Diagram specs on the wire — validation, fences, and the doors a Brain's picture gets.
 *
 * PURE: no React, no DOM — unit-tested in `tests/diagram.test.ts`.
 *
 * A picture reaches a surface two ways, both landing in the same `DiagramSpec`:
 *   1. A FRAME — the runner emits `{ type: 'picture', spec, sources }` while streaming.
 *   2. A FENCE — the stored answer carries the same spec in a ```explainer fenced block, so a
 *      reopened thread re-renders it. `splitDiagramFences` cuts a markdown body into prose and
 *      pictures, and hides an unterminated fence while the reply is still streaming.
 *
 * Model output is never trusted for shape: `validateDiagramSpec` is the only way a spec enters.
 */

import { readDocumentReference } from '../../core/utils/document-reference';
import type {
  BrainPictureData,
  DiagramAction,
  DiagramEdge,
  DiagramMark,
  DiagramMarkKind,
  DiagramNode,
  DiagramSource,
  DiagramSpec,
  DiagramStream,
  DiagramStreamItem,
  DiagramTone,
} from './types';

const KINDS = new Set(['flow', 'layers', 'compare', 'cycle', 'hierarchy', 'timeline', 'hub']);
const MARK_KINDS = new Set<DiagramMarkKind>(['sparkline', 'bars', 'dots']);
export const MARK_MIN_VALUES = 2;
export const MARK_MAX_VALUES = 26;
const TONES = new Set<DiagramTone>(['accent', 'muted', 'warn']);
const MAX_NODES = 12;
// Layout may shorten a preview, but validation must not destroy authored prose: only the
// whitespace around it goes.
const prose = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const clip = (v: unknown, max: number): string | null =>
  typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null;
export const asRecord = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

function cleanAction(raw: unknown): DiagramAction | undefined {
  const a = asRecord(raw);
  const label = a ? clip(a.label, 40) : null;
  if (!a || !label) return undefined;
  switch (a.type) {
    case 'navigate': {
      const catalog = clip(a.catalog, 60);
      if (!catalog) return undefined;
      const params = asRecord(a.params);
      const clean: Record<string, string> = {};
      for (const [k, v] of Object.entries(params ?? {})) {
        if (typeof v === 'string' && v && Object.keys(clean).length < 4)
          clean[k.slice(0, 40)] = v.slice(0, 80);
      }
      return { type: 'navigate', label, catalog, ...(Object.keys(clean).length ? { params: clean } : {}) };
    }
    case 'tour': {
      const tourId = clip(a.tourId, 80);
      return tourId ? { type: 'tour', label, tourId } : undefined;
    }
    case 'ask': {
      const question = clip(a.question, 300);
      return question ? { type: 'ask', label, question } : undefined;
    }
    default:
      // `command` mutates product state and `open` is an arbitrary route — only curated code
      // (the support library, the entity resolver) may carry them.
      return undefined;
  }
}

/**
 * A mark from anything an author wrote — a box, a note card, a frame — cleaned to the contract:
 * a known kind, two to twenty-six finite non-negative values (oldest first), a compare series
 * clipped to the same length and never above its value. Null when there is nothing to draw.
 */
export function parseDiagramMark(raw: unknown): DiagramMark | null {
  const m = asRecord(raw);
  if (!m || typeof m.kind !== 'string' || !MARK_KINDS.has(m.kind as DiagramMarkKind)) return null;
  if (!Array.isArray(m.values)) return null;
  const values: number[] = [];
  for (const v of m.values.slice(0, MARK_MAX_VALUES)) {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
    if (!Number.isFinite(n)) return null;
    values.push(Math.max(0, n));
  }
  if (values.length < MARK_MIN_VALUES) return null;
  let compare: number[] | undefined;
  const rawCompare: unknown = m.compare;
  if (Array.isArray(rawCompare)) {
    compare = values.map((value, i) => {
      const c: unknown = rawCompare[i];
      const n = typeof c === 'number' ? c : typeof c === 'string' && c.trim() ? Number(c) : 0;
      return Number.isFinite(n) ? Math.min(value, Math.max(0, n)) : 0;
    });
    if (!compare.some((c) => c > 0)) compare = undefined;
  }
  const label = clip(m.label, 60);
  return {
    kind: m.kind as DiagramMarkKind,
    values,
    ...(compare ? { compare } : {}),
    ...(label ? { label } : {}),
  };
}

const STREAM_MIN_ITEMS = 2;
const STREAM_MAX_ITEMS = 6;
const STREAM_MAX_NOTES = 4;
const STREAM_MAX_COUNT = 1_000_000;

/**
 * Work moving through the picture, cleaned to the contract: two to six items, each passing at
 * least two of the picture's boxes, with at most four short notes at boxes on its path; a tally
 * of whole, non-negative counts for boxes the picture has. Undefined when nothing honest is left —
 * the picture is still drawn, it just doesn't move items through.
 */
export function cleanDiagramStream(raw: unknown, ids: Set<string>): DiagramStream | undefined {
  const r = asRecord(raw);
  if (!r || !Array.isArray(r.items)) return undefined;
  const unit = asRecord(r.unit);
  const one = unit ? clip(unit.one, 30) : null;
  const many = unit ? clip(unit.many, 30) : null;
  if (!one || !many) return undefined;
  const items: DiagramStreamItem[] = [];
  for (const entry of r.items.slice(0, STREAM_MAX_ITEMS)) {
    const item = asRecord(entry);
    const label = item ? clip(item.label, 60) : null;
    if (!item || !label || !Array.isArray(item.path)) continue;
    const path = item.path
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter((id) => ids.has(id))
      .slice(0, MAX_NODES);
    if (path.length < 2 || new Set(path).size !== path.length) continue;
    const notes: Record<string, string> = {};
    for (const [at, text] of Object.entries(asRecord(item.notes) ?? {})) {
      const note = clip(text, 160);
      if (note && path.includes(at) && Object.keys(notes).length < STREAM_MAX_NOTES) notes[at] = note;
    }
    items.push({ label, path, ...(Object.keys(notes).length ? { notes } : {}) });
  }
  if (items.length < STREAM_MIN_ITEMS) return undefined;
  let tally: DiagramStream['tally'];
  const rawTally = asRecord(r.tally);
  const tallyLabel = rawTally ? clip(rawTally.label, 60) : null;
  if (rawTally && tallyLabel) {
    const counts: Record<string, number> = {};
    for (const [id, value] of Object.entries(asRecord(rawTally.counts) ?? {})) {
      if (!ids.has(id) || typeof value !== 'number' || !Number.isFinite(value)) continue;
      counts[id] = Math.min(STREAM_MAX_COUNT, Math.max(0, Math.round(value)));
    }
    if (Object.keys(counts).length) tally = { label: tallyLabel, counts };
  }
  return { unit: { one, many }, items, ...(tally ? { tally } : {}) };
}

/** A model-authored spec, cleaned to the contract — or null when it cannot be drawn honestly. */
export function validateDiagramSpec(raw: unknown): DiagramSpec | null {
  const r = asRecord(raw);
  if (!r || typeof r.kind !== 'string' || !KINDS.has(r.kind)) return null;
  const kind = r.kind as DiagramSpec['kind'];
  const title = prose(r.title);
  if (!title || !Array.isArray(r.nodes)) return null;
  const nodes: DiagramNode[] = [];
  const ids = new Set<string>();
  for (const item of r.nodes.slice(0, MAX_NODES)) {
    const n = asRecord(item);
    const id = n ? clip(n.id, 40) : null;
    const label = n ? prose(n.label) : null;
    if (!n || !id || !label || ids.has(id)) continue;
    ids.add(id);
    const sub = prose(n.sub);
    const detail = prose(n.detail);
    const documentReference = readDocumentReference(n.documentReference);
    const cardId =
      !documentReference && typeof n.cardId === 'string' && /^[\w-]{1,160}$/.test(n.cardId)
        ? n.cardId
        : undefined;
    const tone =
      typeof n.tone === 'string' && TONES.has(n.tone as DiagramTone) ? (n.tone as DiagramTone) : undefined;
    const catalog = clip(n.catalog, 60);
    const action = cleanAction(n.action);
    const side = n.side === 'right' ? 'right' : n.side === 'left' ? 'left' : undefined;
    const mark = parseDiagramMark(n.mark);
    nodes.push({
      id,
      label,
      ...(sub ? { sub } : {}),
      ...(detail ? { detail } : {}),
      ...(cardId ? { cardId } : {}),
      ...(documentReference ? { documentReference } : {}),
      ...(tone ? { tone } : {}),
      ...(catalog ? { catalog } : {}),
      ...(action ? { action } : {}),
      ...(side ? { side } : {}),
      ...(mark ? { mark } : {}),
    });
  }
  if (nodes.length < 2) return null;
  let edges: DiagramEdge[] | undefined;
  if (Array.isArray(r.edges)) {
    edges = [];
    for (const item of r.edges.slice(0, MAX_NODES * 2)) {
      const e = asRecord(item);
      const from = e ? clip(e.from, 40) : null;
      const to = e ? clip(e.to, 40) : null;
      if (!e || !from || !to || !ids.has(from) || !ids.has(to) || from === to) continue;
      const label = clip(e.label, 40);
      edges.push({
        from,
        to,
        ...(label ? { label } : {}),
        ...(e.style === 'dashed' ? { style: 'dashed' as const } : {}),
      });
    }
  }
  const caption = prose(r.caption);
  const columns =
    Array.isArray(r.columns) && r.columns.length === 2
      ? ([prose(r.columns[0]) ?? '', prose(r.columns[1]) ?? ''] as [string, string])
      : undefined;
  const center = prose(r.center);
  const stream = cleanDiagramStream(r.stream, ids);
  return {
    kind,
    title,
    nodes,
    ...(caption ? { caption } : {}),
    ...(edges ? { edges } : {}),
    ...(columns && columns[0] && columns[1] ? { columns } : {}),
    ...(center ? { center } : {}),
    ...(stream ? { stream } : {}),
  };
}

// ── a Brain's picture ──────────────────────────────────────────────────────────────────────────

const MAX_SOURCES = 6;

function cleanSources(raw: unknown): DiagramSource[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: DiagramSource[] = [];
  for (const item of raw.slice(0, MAX_SOURCES)) {
    const s = asRecord(item);
    const title = s ? clip(s.title, 120) : null;
    if (!title) continue;
    const kind = s ? clip(s.kind, 40) : null;
    out.push({ title, ...(kind ? { kind } : {}) });
  }
  return out.length ? out : undefined;
}

/**
 * The picture a Brain drew, from a `picture` frame (`{ spec, sources }`), a `turn_complete.picture`
 * payload, or a fenced spec (a spec with a `sources` key beside it) — validated; null otherwise.
 */
export function parseBrainPicture(raw: unknown): BrainPictureData | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const r = asRecord(value);
  if (!r) return null;
  const inner = asRecord(r.spec);
  const spec = validateDiagramSpec(inner ?? r);
  if (!spec) return null;
  const sources = cleanSources(r.sources);
  return { spec, ...(sources ? { sources } : {}) };
}

/**
 * Every box is a door, on a Brain surface too: a box without a door asks the Brain about
 * itself. The question names the box and the picture so the Brain knows what the reader means.
 */
export function withAskDoors(spec: DiagramSpec, question = defaultAskQuestion): DiagramSpec {
  let changed = false;
  const nodes = spec.nodes.map((n) => {
    if (n.action) return n;
    changed = true;
    return { ...n, action: { type: 'ask' as const, label: 'Tell me more', question: question(n, spec) } };
  });
  return changed ? { ...spec, nodes } : spec;
}

export function defaultAskQuestion(node: DiagramNode, spec: DiagramSpec): string {
  return `Can you say more about “${node.label}” in “${spec.title}”?`;
}

// ── fences ─────────────────────────────────────────────────────────────────────────────────────

export const DIAGRAM_FENCE = 'explainer';

export type DiagramSegment<T> = { type: 'text'; text: string } | { type: 'figure'; figure: T };

const FENCE_RE = /```explainer[^\n]*\n([\s\S]*?)```/g;
const OPEN_FENCE_RE = /```explainer(?![\s\S]*?```)/;

/**
 * Split a markdown body into prose and pictures. `resolve` turns a fence body into whatever the
 * surface renders (a library picture, a Brain picture); a fence it rejects is dropped, never
 * shown as raw JSON. An UNTERMINATED fence — the reply is still streaming — is cut off, so the
 * reader never sees the JSON being typed.
 */
export function splitDiagramFences<T>(
  text: string,
  resolve: (body: string) => T | null,
): DiagramSegment<T>[] {
  const out: DiagramSegment<T>[] = [];
  let last = 0;
  const pushText = (t: string) => {
    if (t.trim()) out.push({ type: 'text', text: t });
  };
  FENCE_RE.lastIndex = 0;
  for (let m = FENCE_RE.exec(text); m; m = FENCE_RE.exec(text)) {
    pushText(text.slice(last, m.index));
    const figure = resolve(m[1].trim());
    if (figure !== null) out.push({ type: 'figure', figure });
    last = m.index + m[0].length;
  }
  let tail = text.slice(last);
  const open = OPEN_FENCE_RE.exec(tail);
  if (open) tail = tail.slice(0, open.index);
  pushText(tail);
  return out;
}

/** A Brain answer's markdown → its prose (fences removed) and the pictures it carried. */
export function splitBrainPictures(text: string): { text: string; pictures: BrainPictureData[] } {
  if (!text || !text.includes('```' + DIAGRAM_FENCE)) return { text, pictures: [] };
  const segments = splitDiagramFences(text, parseBrainPicture);
  const pictures: BrainPictureData[] = [];
  const prose: string[] = [];
  for (const seg of segments) {
    if (seg.type === 'figure') pictures.push(seg.figure);
    else prose.push(seg.text.trim());
  }
  return { text: prose.join('\n\n').trim(), pictures };
}

/**
 * The rooms' vocabulary on a knowledge-map box (RFC §12.4): a box id of `source:…` / `sources`
 * stands for the Brain's sources, `queue:…` / `draft:…` / `proposal:…` / `needs:…` for what is
 * waiting on the owner. Anything else is a topic, a gap or a miss — an Ask door, nothing more.
 */
export function roomDoorKind(nodeId: string): 'sources' | 'queue' | null {
  if (/^(source|sources)(:|$)/.test(nodeId)) return 'sources';
  if (/^(queue|draft|proposal|needs)(:|$)/.test(nodeId)) return 'queue';
  return null;
}

/**
 * The map's vocabulary on a box id: `pattern:<id>` (a thing people ask about), `source:<document
 * id>` and `queue:<action id>` name a card on the Brain's map, so a box in a reply can light it.
 * A bare `sources` or `queue` names the whole panel (`roomDoorKind`), never one card.
 */
export function mapFocusOf(nodeId: string): { kind: 'pattern' | 'source' | 'queue'; ref: string } | null {
  const match = /^(pattern|topic|source|doc|document|queue|draft|proposal|action|needs):(.+)$/.exec(
    nodeId.trim(),
  );
  if (!match) return null;
  const ref = match[2].trim();
  if (!ref) return null;
  const head = match[1];
  if (head === 'pattern' || head === 'topic') return { kind: 'pattern', ref };
  if (head === 'source' || head === 'doc' || head === 'document') return { kind: 'source', ref };
  return { kind: 'queue', ref };
}
